import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatApiError } from "@/lib/auth";
import { inr } from "@/lib/constants";

const SAMPLE = `type,title,description,respondent_name,respondent_contact,claim_amount
e-commerce,Refund denied for returned phone,Customer returned phone but refund withheld,FastMart India,support@fastmart.in,28000
loan,Excess interest charged,NBFC charged interest above agreed rate,QuickBank NBFC,support@qbnbfc.in,45000
contract,Unpaid invoice for 90 days,Vendor stopped paying after project completion,BuildRight,ops@buildright.in,150000`;

export default function BusinessBulkImport() {
  const [csv, setCsv] = useState(SAMPLE);
  const [preview, setPreview] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  const parse = () => {
    const lines = csv.trim().split(/\r?\n/);
    if (lines.length < 2) return setPreview([]);
    const headers = lines[0].split(",").map(h => h.trim());
    const rows = lines.slice(1).map(line => {
      const cols = line.split(",");
      const obj = {};
      headers.forEach((h, i) => obj[h] = cols[i]?.trim() ?? "");
      obj.claim_amount = Number(obj.claim_amount || 0);
      return obj;
    });
    setPreview(rows);
  };

  const submit = async () => {
    if (preview.length === 0) return toast.error("Parse the CSV first");
    setSubmitting(true);
    try {
      const { data } = await api.post("/cases/bulk", { rows: preview });
      toast.success(`Created ${data.created} cases`);
      nav("/app/cases");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="bulk-import">
      <div>
        <h1 className="font-display text-3xl font-bold text-court">Bulk import cases</h1>
        <p className="text-muted-foreground">Paste a CSV with headers: <code>type,title,description,respondent_name,respondent_contact,claim_amount</code></p>
      </div>
      <Textarea value={csv} onChange={(e) => setCsv(e.target.value)} className="min-h-[200px] font-mono text-xs rounded-sm" data-testid="bulk-csv-input" />
      <div className="flex gap-3">
        <Button variant="outline" onClick={parse} className="rounded-sm" data-testid="bulk-parse">Preview</Button>
        <Button onClick={submit} disabled={submitting || preview.length === 0} className="rounded-sm bg-primary" data-testid="bulk-submit">{submitting ? "Importing…" : `Import ${preview.length} cases`}</Button>
      </div>
      {preview.length > 0 && (
        <div className="bg-card rounded-sm border border-stone/60  overflow-x-auto" data-testid="bulk-preview">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-[0.16em] text-muted-foreground">
              <th className="p-3">Type</th><th className="p-3">Title</th><th className="p-3">Respondent</th><th className="p-3">Claim</th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {preview.map((r, i) => (
                <tr key={i}>
                  <td className="p-3 capitalize">{r.type}</td>
                  <td className="p-3 font-medium">{r.title}</td>
                  <td className="p-3">{r.respondent_name}</td>
                  <td className="p-3 font-semibold">{inr(r.claim_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
